import MirrorBackButton from "@/components/MirrorBackButton";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { Zap, Brain, Leaf, ChevronDown, Droplets, Timer, BookOpen, Sparkles, Watch, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { toast } from "sonner";

// ── Helpers ────────────────────────────────────────────────────────────────────

const todayKey = () => new Date().toISOString().slice(0, 10);

const WEARABLES = [
  "Apple Watch", "Garmin", "Fitbit", "Whoop", "Oura Ring", "Samsung Galaxy Watch",
];

// ── Pillar types ────────────────────────────────────────────────────────────────

type PillarId = "physical" | "mental" | "spiritual";

// ── Sub-components ─────────────────────────────────────────────────────────────

const MirrorCTA = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button onClick={onClick} className="vf-cta w-full justify-center mt-3">
    <Sparkles size={12} />
    <span>{label}</span>
  </button>
);

// ── VitalFlow ──────────────────────────────────────────────────────────────────

const VitalFlow = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [latestCheckin, setLatestCheckin] = useState<any>(null);
  const [openPillar, setOpenPillar] = useState<PillarId | null>("physical");
  const [deviceSheet, setDeviceSheet] = useState(false);

  // Physical — Nutrition
  const [waterCount, setWaterCount] = useState(() => {
    const stored = localStorage.getItem(`vyv-water-${todayKey()}`);
    return stored ? parseInt(stored) : 0;
  });

  // Mental — Focus (Pomodoro)
  const [focusRunning, setFocusRunning] = useState(false);
  const [focusSeconds, setFocusSeconds] = useState(25 * 60);
  const [isBreak, setIsBreak] = useState(false);
  const [focusSessions, setFocusSessions] = useState(() => {
    const stored = localStorage.getItem(`vyv-focus-sessions-${todayKey()}`);
    return stored ? parseInt(stored) : 0;
  });
  const [intention, setIntention] = useState("");
  const focusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mental — Mindset
  const [avoidingText, setAvoidingText] = useState("");

  // Spiritual — Breathing (4-7-8)
  const [breathPhase, setBreathPhase] = useState<"idle" | "inhale" | "hold" | "exhale">("idle");
  const [breathCount, setBreathCount] = useState(() => {
    const stored = localStorage.getItem(`vyv-breath-${todayKey()}`);
    return stored ? parseInt(stored) : 0;
  });
  const breathTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Spiritual — Alignment
  const [alignment, setAlignment] = useState<"yes" | "partially" | "no" | null>(() => {
    return (localStorage.getItem(`vyv-alignment-${todayKey()}`) as any) || null;
  });

  // Spiritual — Gratitude
  const [gratitudeText, setGratitudeText] = useState("");
  const [gratitudeList, setGratitudeList] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("vyv-gratitude") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.from("daily_checkins").select("*")
          .eq("user_id", user.id).not("checked_in_at", "is", null)
          .lte("checked_in_at", new Date().toISOString())
          .order("checked_in_at", { ascending: false }).limit(1).maybeSingle();
        if (data) setLatestCheckin(data);
      }
    };
    load();
  }, []);

  // ── Focus timer ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (focusRunning) {
      focusIntervalRef.current = setInterval(() => {
        setFocusSeconds((s) => {
          if (s <= 1) {
            clearInterval(focusIntervalRef.current!);
            setFocusRunning(false);
            if (!isBreak) {
              const next = focusSessions + 1;
              setFocusSessions(next);
              localStorage.setItem(`vyv-focus-sessions-${todayKey()}`, String(next));
              setIsBreak(true);
              setFocusSeconds(5 * 60);
            } else {
              setIsBreak(false);
              setFocusSeconds(25 * 60);
            }
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } else {
      if (focusIntervalRef.current) clearInterval(focusIntervalRef.current);
    }
    return () => { if (focusIntervalRef.current) clearInterval(focusIntervalRef.current); };
  }, [focusRunning]);

  const resetFocus = () => {
    setFocusRunning(false);
    setIsBreak(false);
    setFocusSeconds(25 * 60);
  };

  const fmtTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // ── Breathing ─────────────────────────────────────────────────────────────────

  const stopBreath = useCallback(() => {
    if (breathTimerRef.current) clearTimeout(breathTimerRef.current);
    setBreathPhase("idle");
  }, []);

  const startBreathCycle = useCallback(() => {
    setBreathPhase("inhale");
    breathTimerRef.current = setTimeout(() => {
      setBreathPhase("hold");
      breathTimerRef.current = setTimeout(() => {
        setBreathPhase("exhale");
        breathTimerRef.current = setTimeout(() => {
          const next = breathCount + 1;
          setBreathCount(next);
          localStorage.setItem(`vyv-breath-${todayKey()}`, String(next));
          setBreathPhase("idle");
        }, 8000);
      }, 7000);
    }, 4000);
  }, [breathCount]);

  useEffect(() => {
    return () => { if (breathTimerRef.current) clearTimeout(breathTimerRef.current); };
  }, []);

  // ── Water ─────────────────────────────────────────────────────────────────────

  const addWater = () => {
    const next = waterCount + 1;
    setWaterCount(next);
    localStorage.setItem(`vyv-water-${todayKey()}`, String(next));
  };

  // ── Gratitude ─────────────────────────────────────────────────────────────────

  const saveGratitude = () => {
    if (!gratitudeText.trim()) return;
    const next = [gratitudeText.trim(), ...gratitudeList].slice(0, 10);
    setGratitudeList(next);
    localStorage.setItem("vyv-gratitude", JSON.stringify(next));
    setGratitudeText("");
    toast.success(t("vitalflow.gratitudeSaved"));
  };

  // ── Mirror navigation ─────────────────────────────────────────────────────────

  const toMirror = (msg?: string) => {
    if (msg) sessionStorage.setItem("vyv-prefill", msg);
    navigate("/mirror");
  };

  // ── Pillar config ─────────────────────────────────────────────────────────────

  const energy = latestCheckin?.energy ?? 3;

  const pillars: { id: PillarId; label: string; icon: React.ReactNode; color: string; hue: string }[] = [
    { id: "physical", label: t("vitalflow.physical"), icon: <Zap size={15} />, color: "hsl(187 72% 50%)", hue: "187 72% 50%" },
    { id: "mental", label: t("vitalflow.mental"), icon: <Brain size={15} />, color: "hsl(270 55% 65%)", hue: "270 55% 65%" },
    { id: "spiritual", label: t("vitalflow.spiritual"), icon: <Leaf size={15} />, color: "hsl(40 90% 60%)", hue: "40 90% 60%" },
  ];

  const breathLabel = breathPhase === "inhale" ? t("vitalflow.inhale")
    : breathPhase === "hold" ? t("vitalflow.hold")
    : breathPhase === "exhale" ? t("vitalflow.exhale")
    : "";

  const breathScale = breathPhase === "inhale" ? 1.3 : breathPhase === "hold" ? 1.3 : breathPhase === "exhale" ? 1 : 1;
  const breathDuration = breathPhase === "inhale" ? 4 : breathPhase === "hold" ? 7 : breathPhase === "exhale" ? 8 : 0.3;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <MobileLayout className="pb-24 pt-6 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[380px] h-[240px] rounded-full opacity-[0.03]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} />
      </div>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-6 relative z-10">
        <MirrorBackButton />
        <div className="flex-1 ml-3">
          <p className="vyv-label mb-0.5">{t("vitalflow.yourSystem")}</p>
          <h1 className="font-display text-[22px] font-semibold text-foreground leading-none">
            {t("vitalflow.title")}<span className="text-primary">.</span>
          </h1>
          <p className="text-muted-foreground text-[11px] mt-1 font-body">{t("vitalflow.subtitle")}</p>
        </div>
        <button
          onClick={() => setDeviceSheet(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground/[0.05] border border-foreground/[0.08] text-foreground/50 text-[11px] font-display hover:bg-foreground/[0.08] transition-colors"
        >
          <Watch size={12} />
          <span>{t("vitalflow.connectDevice")}</span>
        </button>
      </motion.div>

      {/* Pillars */}
      <div className="space-y-3 relative z-10">
        {pillars.map((pillar, pi) => {
          const isOpen = openPillar === pillar.id;
          return (
            <motion.div key={pillar.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: pi * 0.07, duration: 0.5 }}
              className="vyv-card overflow-hidden"
              style={{ borderLeft: `3px solid hsl(${pillar.hue} / ${isOpen ? "0.5" : "0.2"})`, transition: "border-color 0.3s" }}>

              {/* Pillar header */}
              <button
                onClick={() => setOpenPillar(isOpen ? null : pillar.id)}
                className="w-full flex items-center gap-3 px-4 py-4 text-left"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `hsl(${pillar.hue} / 0.1)`, color: pillar.color }}>
                  {pillar.icon}
                </div>
                <p className="font-display text-[14px] font-semibold text-foreground flex-1">{pillar.label}</p>
                <ChevronDown size={14} className="text-foreground/30 transition-transform duration-300"
                  style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
              </button>

              {/* Pillar content */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-5 space-y-5 border-t border-foreground/[0.05] pt-4">

                      {/* ── PHYSICAL ── */}
                      {pillar.id === "physical" && (
                        <>
                          {/* Training */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.trainingModule")}</p>
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex-1">
                                <p className="text-foreground/70 text-[12px] font-body">
                                  {t("vitalflow.bodyEnergy")}
                                </p>
                                <div className="flex gap-1 mt-1.5">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i < energy ? "bg-primary/60" : "bg-foreground/[0.06]"}`} />
                                  ))}
                                </div>
                              </div>
                              <span className="font-display text-[20px] font-semibold text-primary/60">{energy}/5</span>
                            </div>
                            <MirrorCTA label={t("vitalflow.talkAboutTraining")} onClick={() => toMirror("Let's talk about my training today.")} />
                          </div>

                          {/* Nutrition */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.nutritionModule")}</p>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Droplets size={14} className="text-primary/50" />
                                <span className="text-foreground/70 text-[12px] font-body">{t("vitalflow.waterIntake")}</span>
                              </div>
                              <span className="font-display text-[13px] font-semibold text-primary/70">
                                {waterCount}/8 {t("vitalflow.glasses")}
                              </span>
                            </div>
                            <div className="flex gap-1 mb-3">
                              {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className={`h-2 flex-1 rounded-full transition-all duration-300 ${i < waterCount ? "bg-primary/60" : "bg-foreground/[0.06]"}`} />
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <button onClick={addWater} className="vf-cta flex-1 justify-center">
                                <Droplets size={12} />
                                <span>{t("vitalflow.addGlass")}</span>
                              </button>
                            </div>
                          </div>

                          {/* Recovery */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.recoveryModule")}</p>
                            <p className="text-foreground/55 text-[12px] font-body leading-relaxed mb-1">
                              {energy >= 4 ? t("vitalflow.recoveryHigh") : energy >= 3 ? t("vitalflow.recoveryMid") : t("vitalflow.recoveryLow")}
                            </p>
                            <MirrorCTA label={t("vitalflow.askRecovery")} onClick={() => toMirror("How should I optimize my recovery today?")} />
                          </div>
                        </>
                      )}

                      {/* ── MENTAL ── */}
                      {pillar.id === "mental" && (
                        <>
                          {/* Focus — Pomodoro */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.focusModule")}</p>
                            <div className="flex items-center gap-4 mb-3">
                              <div className="relative w-16 h-16 shrink-0">
                                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                                  <circle cx="32" cy="32" r="26" fill="none" stroke="hsl(270 55% 65% / 0.1)" strokeWidth="3" />
                                  <circle cx="32" cy="32" r="26" fill="none" stroke="hsl(270 55% 65% / 0.6)" strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeDasharray={`${2 * Math.PI * 26}`}
                                    strokeDashoffset={`${2 * Math.PI * 26 * (1 - focusSeconds / ((isBreak ? 5 : 25) * 60))}`}
                                    style={{ transition: "stroke-dashoffset 1s linear" }} />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="font-display text-[11px] font-semibold text-foreground/80">{fmtTime(focusSeconds)}</span>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-foreground/60 text-[11px] font-body mb-2">
                                  {isBreak ? t("vitalflow.focusBreak") : t("vitalflow.focusWork")} · {focusSessions} {t("vitalflow.focusSessions")}
                                </p>
                                <input
                                  value={intention}
                                  onChange={(e) => setIntention(e.target.value)}
                                  placeholder={t("vitalflow.setIntention")}
                                  className="w-full bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg px-3 py-2 text-[11px] font-body text-foreground/70 placeholder:text-foreground/25 focus:outline-none"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setFocusRunning(!focusRunning)}
                                className="vf-cta flex-1 justify-center"
                                style={{ color: "hsl(270 55% 70%)", borderColor: "hsl(270 55% 65% / 0.2)", background: "hsl(270 55% 65% / 0.07)" }}
                              >
                                <Timer size={12} />
                                <span>{focusRunning ? t("vitalflow.pauseFocus") : t("vitalflow.startFocus")}</span>
                              </button>
                              <button onClick={resetFocus}
                                className="px-3 py-2 rounded-xl text-[11px] font-display text-foreground/35 border border-foreground/[0.08] bg-foreground/[0.03] hover:bg-foreground/[0.06] transition-colors">
                                {t("vitalflow.resetFocus")}
                              </button>
                            </div>
                          </div>

                          {/* Learning */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.learningModule")}</p>
                            <p className="text-foreground/50 text-[12px] font-body mb-1">{t("vitalflow.whatToLearn")}</p>
                            <MirrorCTA label={t("vitalflow.talkToMirror")} onClick={() => toMirror("What should I be learning right now to grow fastest?")}
                            />
                          </div>

                          {/* Mindset */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.mindsetModule")}</p>
                            <p className="text-foreground/50 text-[11px] font-body mb-2">{t("vitalflow.avoidingToday")}</p>
                            <textarea
                              value={avoidingText}
                              onChange={(e) => setAvoidingText(e.target.value)}
                              placeholder="..."
                              className="w-full bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl px-3 py-2.5 text-[12px] font-body text-foreground/70 placeholder:text-foreground/25 focus:outline-none resize-none min-h-[60px]"
                            />
                            {avoidingText.trim() && (
                              <MirrorCTA label={t("vitalflow.talkToMirror")} onClick={() => toMirror(`I've been avoiding: ${avoidingText}`)} />
                            )}
                          </div>
                        </>
                      )}

                      {/* ── SPIRITUAL / ALIGNMENT ── */}
                      {pillar.id === "spiritual" && (
                        <>
                          {/* Breathing — 4-7-8 */}
                          <div>
                            <p className="vyv-label mb-3">{t("vitalflow.meditationModule")}</p>
                            <div className="flex flex-col items-center gap-4">
                              <motion.div
                                animate={{ scale: breathScale }}
                                transition={{ duration: breathDuration, ease: "easeInOut" }}
                                className="w-20 h-20 rounded-full flex items-center justify-center"
                                style={{
                                  background: "radial-gradient(circle, hsl(40 90% 60% / 0.12), hsl(40 90% 60% / 0.04))",
                                  border: "1.5px solid hsl(40 90% 60% / 0.3)",
                                  boxShadow: breathPhase !== "idle" ? "0 0 30px hsl(40 90% 60% / 0.15)" : "none",
                                }}
                              >
                                <AnimatePresence mode="wait">
                                  {breathPhase !== "idle" ? (
                                    <motion.p key={breathPhase} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                      className="font-display text-[11px] font-semibold text-center"
                                      style={{ color: "hsl(40 90% 65%)" }}>
                                      {breathLabel}
                                    </motion.p>
                                  ) : (
                                    <motion.p key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                      className="font-display text-[10px] text-foreground/30">
                                      4-7-8
                                    </motion.p>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                              <div className="flex items-center gap-3">
                                {breathPhase === "idle" ? (
                                  <button onClick={startBreathCycle}
                                    className="px-5 py-2 rounded-xl text-[12px] font-display font-semibold transition-colors"
                                    style={{ background: "hsl(40 90% 60% / 0.1)", border: "1px solid hsl(40 90% 60% / 0.2)", color: "hsl(40 90% 65%)" }}>
                                    {t("vitalflow.startBreathing")}
                                  </button>
                                ) : (
                                  <button onClick={stopBreath}
                                    className="px-5 py-2 rounded-xl text-[12px] font-display text-foreground/40 bg-foreground/[0.04] border border-foreground/[0.08] transition-colors">
                                    {t("vitalflow.stopBreathing")}
                                  </button>
                                )}
                                {breathCount > 0 && (
                                  <span className="text-[11px] font-display text-foreground/30">{breathCount} {t("vitalflow.breathSessions")}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Alignment check */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.alignmentModule")}</p>
                            <p className="text-foreground/55 text-[12px] font-body mb-3">{t("vitalflow.alignedToday")}</p>
                            <div className="flex gap-2">
                              {(["yes", "partially", "no"] as const).map((val) => {
                                const labels = { yes: t("vitalflow.alignedYes"), partially: t("vitalflow.alignedPartially"), no: t("vitalflow.alignedNo") };
                                const isSelected = alignment === val;
                                return (
                                  <button key={val}
                                    onClick={() => {
                                      setAlignment(val);
                                      localStorage.setItem(`vyv-alignment-${todayKey()}`, val);
                                    }}
                                    className="flex-1 py-2.5 rounded-xl text-[11px] font-display font-semibold transition-all duration-200"
                                    style={{
                                      background: isSelected ? "hsl(40 90% 60% / 0.12)" : "hsl(0 0% 100% / 0.03)",
                                      border: `1px solid ${isSelected ? "hsl(40 90% 60% / 0.35)" : "hsl(0 0% 100% / 0.07)"}`,
                                      color: isSelected ? "hsl(40 90% 65%)" : "hsl(0 0% 100% / 0.45)",
                                    }}>
                                    {labels[val]}
                                  </button>
                                );
                              })}
                            </div>
                            {alignment === "no" && (
                              <MirrorCTA label={t("vitalflow.talkToMirror")} onClick={() => toMirror("I feel off track today. Help me realign.")} />
                            )}
                          </div>

                          {/* Gratitude */}
                          <div>
                            <p className="vyv-label mb-2">{t("vitalflow.gratitudeModule")}</p>
                            <div className="flex gap-2 mb-3">
                              <input
                                value={gratitudeText}
                                onChange={(e) => setGratitudeText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && saveGratitude()}
                                placeholder={t("vitalflow.gratitudePlaceholder")}
                                className="flex-1 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl px-3 py-2.5 text-[12px] font-body text-foreground/70 placeholder:text-foreground/25 focus:outline-none"
                              />
                              <button onClick={saveGratitude}
                                className="px-3 py-2 rounded-xl text-[11px] font-display font-semibold transition-colors"
                                style={{ background: "hsl(40 90% 60% / 0.1)", border: "1px solid hsl(40 90% 60% / 0.2)", color: "hsl(40 90% 65%)" }}>
                                +
                              </button>
                            </div>
                            {gratitudeList.slice(0, 3).map((entry, i) => (
                              <p key={i} className="text-foreground/40 text-[11px] font-body leading-relaxed mb-1">
                                · {entry}
                              </p>
                            ))}
                          </div>
                        </>
                      )}

                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Device bottom sheet */}
      <AnimatePresence>
        {deviceSheet && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40" onClick={() => setDeviceSheet(false)} />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-[hsl(222_38%_7%)] border-t border-foreground/[0.08] rounded-t-2xl pb-10 pt-5"
            >
              <div className="flex items-center justify-between px-5 mb-4">
                <p className="font-display text-[14px] font-semibold text-foreground">{t("vitalflow.connectDevice")}</p>
                <button onClick={() => setDeviceSheet(false)} className="text-foreground/40 hover:text-foreground/70 transition-colors">
                  <X size={16} />
                </button>
              </div>
              <div className="space-y-1 px-4">
                {WEARABLES.map((w) => (
                  <button key={w} onClick={() => { toast.info(t("vitalflow.comingSoonFeature")); setDeviceSheet(false); }}
                    className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl hover:bg-foreground/[0.04] transition-colors text-left">
                    <span className="font-display text-[13px] text-foreground/75">{w}</span>
                    <span className="text-[11px] font-display text-primary/50">{t("vitalflow.comingSoonFeature")}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </MobileLayout>
  );
};

export default VitalFlow;
